import { classOf, classSkills, OATH_BY_ID, OATH_SLOTS, OATHS, parseOaths, rarityPips, SHAPE_GLYPHS, SIMPATIA_BY_ID, SIMPATIA_RARITY_NAMES, SKILL_BY_ID, type SkillDef } from "@eldrun/content";

export interface BlessingRow {
  id: string;
  name: string;
  /** ◆ a ◆◆◆ (F60: raridade sem cor). */
  pips: string;
  stacks: number;
  description: string;
  /** Raridade pelo nome e o nível (1 comum, 2 rara, 3 lendária) para os pips do detalhe (F82). */
  rarity: string;
  tier: number;
  /** O efeito com mais uma pilha (o retorno decrescente das Bênçãos). */
  next: string;
}

export interface StatRow {
  label: string;
  value: string;
  /** Um juramento feito mexeu neste número. */
  boosted: boolean;
}

export interface SkillRow {
  id: string;
  /** Passiva, Ativa ou Ultimate. */
  kind: string;
  name: string;
  description: string;
  key: string;
  /** Juramentos feitos nesta habilidade e quantos ela tem (os pips do detalhe, F82); a passiva não tem. */
  level: number;
  levels: number;
  /** Recarga, carga e os números da regra, já com os juramentos. */
  stats: StatRow[];
}

export interface OathRow {
  id: string;
  name: string;
  description: string;
  /** A habilidade que o juramento melhora (F82). */
  skill: string;
}

export interface AbilitiesView {
  className: string;
  /** Ícone da classe pela forma (⬡ △ ◯ ≡). */
  classGlyph: string;
  blessings: BlessingRow[];
  skills: SkillRow[];
  /** Os 5 lugares do Juramento: feito (o nome) ou vazio. */
  oaths: (OathRow | undefined)[];
  /** A oferta aberta (2 opções, ou nenhuma). */
  offer: OathRow[];
}

const oathRow = (id: string): OathRow | undefined => {
  const o = OATH_BY_ID.get(id);
  return o && { id, name: o.name, description: o.description, skill: SKILL_BY_ID.get(o.skill)?.name ?? o.skill };
};

/** Número com vírgula, sem casas à toa (2,5; 12). */
const num = (v: number): string => String(Math.round(v * 10) / 10).replace(".", ",");

/** Como cada número da regra aparece no detalhe (os que não estão aqui ficam de fora: ritmo interno). */
const PARAM_ROWS: Readonly<Record<string, { label: string; format: (v: number) => string }>> = {
  radius: { label: "Raio", format: (v) => `${num(v)} m` },
  range: { label: "Alcance", format: (v) => `${num(v)} m` },
  distance: { label: "Distância", format: (v) => `${num(v)} m` },
  length: { label: "Comprimento", format: (v) => `${num(v)} m` },
  width: { label: "Largura", format: (v) => `${num(v)} m` },
  damage: { label: "Dano", format: num },
  heal: { label: "Cura", format: (v) => `${num(v)} de Vida/s` },
  durationMs: { label: "Duração", format: (v) => `${num(v / 1000)} s` },
  tauntMs: { label: "Provocação", format: (v) => `${num(v / 1000)} s` },
  guardMul: { label: "Dano recebido", format: (v) => `−${Math.round((1 - v) * 100)} %` },
  invulnMs: { label: "Invulnerável", format: (v) => `${num(v / 1000)} s` },
  rootMs: { label: "Prende", format: (v) => `${num(v / 1000)} s` },
  slowMs: { label: "Lento", format: (v) => `${num(v / 1000)} s` },
  markMs: { label: "Marca", format: (v) => `${num(v / 1000)} s` },
  exposeMs: { label: "Exposto", format: (v) => `${num(v / 1000)} s` },
  windupMs: { label: "Preparo", format: (v) => `${num(v / 1000)} s` },
  reviveFraction: { label: "Revive com", format: (v) => `${Math.round(v * 100)} % de Vida` },
  arrowPierce: { label: "Flechas perfuram", format: (v) => `${num(v)} alvos` },
};

/** Recarga (ou carga) e os números da regra de uma habilidade com os juramentos feitos somados. */
export function skillStats(skill: SkillDef, taken: readonly string[]): StatRow[] {
  const bonus = new Map<string, number>();
  for (const id of taken) {
    const o = OATH_BY_ID.get(id);
    if (o?.skill === skill.id) bonus.set(o.param, (bonus.get(o.param) ?? 0) + o.add);
  }
  const rows: StatRow[] = [];
  if (skill.cooldownMs !== undefined) {
    // A recarga nunca cai abaixo de 1 s (F70).
    const add = bonus.get("cooldownMs") ?? 0;
    rows.push({ label: "Recarga", value: `${num(Math.max(1000, skill.cooldownMs + add) / 1000)} s`, boosted: add !== 0 });
  }
  if (skill.charge !== undefined) rows.push({ label: "Carga", value: String(skill.charge), boosted: false });
  for (const [param, value] of Object.entries(skill.params ?? {})) {
    const look = PARAM_ROWS[param];
    if (!look) continue;
    const add = bonus.get(param) ?? 0;
    rows.push({ label: look.label, value: look.format(value + add), boosted: add !== 0 });
  }
  return rows;
}

const RARITY_TIER = { comum: 1, rara: 2, lendaria: 3 } as const;

/**
 * Painel de Bênçãos e Habilidades (F70, doc 09 §5.2, mockup "folha e raio"; F82 no `eldrun-ui-d.png`):
 * as Bênçãos com pilhas, raridade e o próximo efeito; a passiva, a ativa e a ultimate da classe com os
 * números já com os juramentos; os 5 lugares do Juramento da Alvorada e a oferta aberta. Projeção pura.
 */
export function abilitiesView(classId: string, simpatias: Iterable<[string, number]>, oaths: string, offer: readonly string[], keys: { ativa: string; ultimate: string }): AbilitiesView {
  const c = classOf(classId);
  const s = classSkills(c);
  const blessings: BlessingRow[] = [];
  for (const [id, n] of simpatias) {
    const def = SIMPATIA_BY_ID.get(id);
    if (def && n > 0) blessings.push({ id, name: def.name, pips: rarityPips(def.rarity), stacks: n, description: def.describe(n), rarity: SIMPATIA_RARITY_NAMES[def.rarity], tier: RARITY_TIER[def.rarity], next: def.describe(n + 1) });
  }
  blessings.sort((a, b) => b.pips.length - a.pips.length || a.name.localeCompare(b.name, "pt-BR"));
  const taken = parseOaths(oaths);
  const skill = (k: SkillDef, kind: string, key: string): SkillRow => {
    const levels = OATHS.filter((o) => o.skill === k.id).length;
    return { id: k.id, kind, name: k.name, description: k.description, key, level: taken.filter((id) => OATH_BY_ID.get(id)?.skill === k.id).length, levels, stats: skillStats(k, taken) };
  };
  return {
    className: c.name,
    classGlyph: SHAPE_GLYPHS[c.shape],
    blessings,
    skills: [skill(s.passiva, "Passiva", ""), skill(s.ativa, "Ativa", keys.ativa), skill(s.ultimate, "Ultimate", keys.ultimate)],
    oaths: Array.from({ length: OATH_SLOTS }, (_, i) => (taken[i] ? oathRow(taken[i]) : undefined)),
    offer: offer.map(oathRow).filter((o): o is OathRow => o !== undefined),
  };
}
